// =============================================================================
// trains.ts – Train connection computation (pure logic)
// =============================================================================

import { TRAIN_ROUTES, TRAIN_STATIONS, TRAIN_DEPARTURES } from "../data/trains.js";
import type { TrainResult, TrainSlot } from "./types.js";

// ─── Types ──────────────────────────────────────────────────────────────────

interface FixedTrain {
  departureMin: number;
  line: string;
  note: string;
}

interface TrainSlotEntry {
  line?: string;
  minutes?: number[];
  firstTrain?: number;
  lastTrain?: number;
  note?: string;
  fixedTrains?: FixedTrain[];
}

interface CanegrateBlock {
  driveMinutes?: number;
  canLeaveIn: number | null;
  arrivalAtStation?: number;
  trains: TrainResult[];
  justMissedCarTrain: TrainResult | null;
  milanoArrival?: number;
}

interface StationBlock {
  reachMinutes?: number;
  canLeaveIn: number | null;
  arrivalAtStation?: number;
  trains: TrainResult[];
  justMissedCarTrain: TrainResult | null;
  milanoArrival?: number;
}

// ─── Train Slots ────────────────────────────────────────────────────────────

/**
 * TRAIN_SLOTS defines fallback slot intervals for trains at stations or lines
 * not yet covered by raw GTFS departures. For fully integrated routes, GTFS-based
 * departures from `TRAIN_DEPARTURES` are used instead.
 */
const TRAIN_SLOTS: Record<string, TrainSlotEntry> = {
  PG102: {
    line: "S6",
    minutes: [15, 45],
    firstTrain: 315,
    lastTrain: 1425,
    note: "dir. Milano Passante"
  },
  canegrate: {
    line: "S5",
    minutes: [21, 51],
    firstTrain: 351,
    lastTrain: 1401,
    note: "da Canegrate FS dir. Milano"
  },
  LG090: {
    line: "S5",
    minutes: [12, 42],
    firstTrain: 312,
    lastTrain: 1362,
    note: "dir. Milano Passante"
  },
  PB090: {
    line: "S5",
    minutes: [13, 43],
    firstTrain: 343,
    lastTrain: 1423,
    note: "dir. Milano Passante"
  },
  BS090_S5: {
    line: "S5",
    minutes: [3, 33],
    firstTrain: 303,
    lastTrain: 1383,
    note: "dir. Milano Passante"
  },
  BS090_RE: {
    line: "RE",
    minutes: [20, 50],
    firstTrain: 380,
    lastTrain: 1370,
    note: "dir. Milano"
  },
  LG090_RE: {
    fixedTrains: [
      { departureMin: 362, line: "RE", note: "2511 dir. Milano P. Garibaldi" },
      { departureMin: 432, line: "RE", note: "2513 dir. Milano P. Garibaldi" },
      { departureMin: 447, line: "RE", note: "Luino → Milano P. Garibaldi" },
      { departureMin: 473, line: "RE", note: "Domodossola → Milano P. Garibaldi" },
      { departureMin: 482, line: "RE", note: "2515 dir. Milano P. Garibaldi" },
      { departureMin: 515, line: "RE", note: "Varese → Milano P. Garibaldi" },
      { departureMin: 542, line: "RE", note: "2517 dir. Milano P. Garibaldi" },
      { departureMin: 602, line: "RE", note: "2519 dir. Milano P. Garibaldi" },
      { departureMin: 662, line: "RE", note: "2521 dir. Milano P. Garibaldi" },
      { departureMin: 722, line: "RE", note: "2523 dir. Milano P. Garibaldi" },
      { departureMin: 782, line: "RE", note: "2525 dir. Milano P. Garibaldi" },
      { departureMin: 842, line: "RE", note: "2527 dir. Milano P. Garibaldi" },
      { departureMin: 902, line: "RE", note: "2529 dir. Milano P. Garibaldi" },
      { departureMin: 962, line: "RE", note: "2531 dir. Milano P. Garibaldi" },
      { departureMin: 1022, line: "RE", note: "2533 dir. Milano P. Garibaldi" },
      { departureMin: 1082, line: "RE", note: "2535 dir. Milano P. Garibaldi" },
      { departureMin: 1142, line: "RE", note: "2537 dir. Milano P. Garibaldi" },
      { departureMin: 1172, line: "RE", note: "Varese → Milano P. Garibaldi" },
      { departureMin: 1202, line: "RE", note: "2539 dir. Milano P. Garibaldi" },
      { departureMin: 1262, line: "RE", note: "2541 dir. Milano P. Garibaldi" },
      { departureMin: 1322, line: "RE", note: "2543 dir. Milano P. Garibaldi" },
      { departureMin: 1397, line: "RE", note: "Varese → Milano P. Garibaldi" }
    ]
  }
};

// ─── Functions ──────────────────────────────────────────────────────────────

export function calcNextTrain(stopKey: string, fromMinutes: number, count = 2): TrainResult[] {
  const slot = TRAIN_SLOTS[stopKey];
  if (!slot) return [];

  const results: TrainResult[] = [];

  if (slot.minutes) {
    const hour = Math.floor(fromMinutes / 60);
    for (let h = hour; h < 24; h++) {
      for (const m of slot.minutes) {
        const depMin = h * 60 + m;
        if (depMin <= fromMinutes) continue;
        if (slot.firstTrain && depMin < slot.firstTrain) continue;
        if (slot.lastTrain && depMin > slot.lastTrain) continue;
        results.push({
          line: slot.line || "Treno",
          departureMin: depMin,
          waitMin: depMin - fromMinutes,
          note: slot.note || ""
        });
      }
    }
  }

  if (slot.fixedTrains) {
    for (const f of slot.fixedTrains) {
      if (f.departureMin <= fromMinutes) continue;
      results.push({
        line: f.line || slot.line || "Treno",
        departureMin: f.departureMin,
        waitMin: f.departureMin - fromMinutes,
        note: f.note || slot.note || ""
      });
    }
  }

  results.sort((a, b) => a.departureMin - b.departureMin);
  return results.slice(0, count);
}

export function getConnectionInfo(
  stopKey: string,
  busArrivalMin: number,
  connectionConfig: any
): {
  type: string;
  line: string;
  departure: number;
  waitMin: number;
  allTrains: TrainResult[];
} | null {
  if (!connectionConfig) return null;
  const trains = calcNextTrain(connectionConfig.slotKey || stopKey, busArrivalMin);
  if (trains.length === 0) return null;
  const best = trains[0];
  return {
    type: connectionConfig.type,
    line: best.line,
    departure: best.departureMin,
    waitMin: best.waitMin,
    allTrains: trains
  };
}

export function formatConnection(
  conn: { type: string; departure: number; waitMin: number } | null
): string {
  if (!conn) return "";
  const h = Math.floor(conn.departure / 60);
  const m = String(conn.departure % 60).padStart(2, "0");
  const dep = `${h}:${m}`;
  if (conn.waitMin <= 0) return `${conn.type} ${dep}`;
  return `${conn.type} ${dep} (~${conn.waitMin}m)`;
}

export function buildCanegrateBlock(driveMinutes: number, currentMin: number): CanegrateBlock {
  const arrivalAtStation = currentMin + driveMinutes;
  const allFutureTrains = calcNextTrain("canegrate", currentMin);
  const catchableTrains = calcNextTrain("canegrate", arrivalAtStation);

  let justMissedCarTrain: TrainResult | null = null;
  if (
    allFutureTrains.length > 0 &&
    catchableTrains.length > 0 &&
    allFutureTrains[0].departureMin !== catchableTrains[0].departureMin
  ) {
    justMissedCarTrain = allFutureTrains[0];
  } else if (allFutureTrains.length > 0 && catchableTrains.length === 0) {
    justMissedCarTrain = allFutureTrains[0];
  }

  if (catchableTrains.length === 0) return { canLeaveIn: null, trains: [], justMissedCarTrain };

  const firstTrain = catchableTrains[0];
  const leaveIn = firstTrain.departureMin - driveMinutes - currentMin;

  return {
    driveMinutes,
    canLeaveIn: leaveIn > 0 ? leaveIn : 0,
    arrivalAtStation,
    trains: catchableTrains,
    justMissedCarTrain,
    milanoArrival: firstTrain.departureMin + 25
  };
}

export function calcNextTrainFromGTFS(
  stationCode: string,
  direction: string,
  dayType: string,
  fromMinutes: number,
  count = 2
): TrainResult[] {
  const stationData = (TRAIN_DEPARTURES as Record<string, any>)[stationCode];
  if (!stationData) return [];

  const results: TrainResult[] = [];
  const key = `${dayType}_${direction}`;

  for (const [routeId, schedules] of Object.entries(stationData as Record<string, any>)) {
    const times: number[] = schedules[key] || [];
    const routeInfo = (TRAIN_ROUTES as Record<string, any>)[routeId] || {
      name: routeId,
      color: "#666"
    };
    for (const min of times) {
      if (min > fromMinutes) {
        results.push({
          line: routeInfo.name,
          color: routeInfo.color,
          departureMin: min,
          waitMin: min - fromMinutes,
          note: direction === "to_milano" ? "dir. Milano" : "da Milano"
        });
      }
    }
  }

  results.sort((a, b) => a.departureMin - b.departureMin);
  return results.slice(0, count);
}

export function getTravelTimeToMilano(stationCode: string): number {
  const times: Record<string, number> = {
    CN_FS: 28,
    PB_FS: 24,
    LG_FS: 32,
    BS_FS: 35,
    PG_FS: 19,
    VZ_FS: 22,
    RH_FS: 15,
    BSN_FS: 32
  };
  return times[stationCode] || 25;
}

export function buildTrainStationBlock(
  stationCode: string,
  reachMinutes: number,
  currentMin: number,
  dayType: string,
  direction = "to_milano"
): StationBlock {
  const arrivalAtStation = currentMin + reachMinutes;
  const allFutureTrains = calcNextTrainFromGTFS(stationCode, direction, dayType, currentMin);
  const catchableTrains = calcNextTrainFromGTFS(stationCode, direction, dayType, arrivalAtStation);

  let justMissedCarTrain: TrainResult | null = null;
  if (
    allFutureTrains.length > 0 &&
    catchableTrains.length > 0 &&
    allFutureTrains[0].departureMin !== catchableTrains[0].departureMin
  ) {
    justMissedCarTrain = allFutureTrains[0];
  } else if (allFutureTrains.length > 0 && catchableTrains.length === 0) {
    justMissedCarTrain = allFutureTrains[0];
  }

  if (catchableTrains.length === 0) return { canLeaveIn: null, trains: [], justMissedCarTrain };

  const firstTrain = catchableTrains[0];
  const leaveIn = firstTrain.departureMin - reachMinutes - currentMin;
  const travelTime = getTravelTimeToMilano(stationCode);

  return {
    reachMinutes,
    canLeaveIn: leaveIn > 0 ? leaveIn : 0,
    arrivalAtStation,
    trains: catchableTrains,
    justMissedCarTrain,
    milanoArrival: firstTrain.departureMin + travelTime
  };
}

export { TRAIN_SLOTS };
